import Header from "../../../../components/Header/Header";
import { visibleScrollbar } from "../../../../utils/functions/get-visible-scrollbar";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { AllTickets } from "./components/AllTickets/AllTickets";
import { CreateTicket } from "./components/CreateTicket/CreateTicket";
import { TicketReports } from "./components/TicketReports/TicketReports";
import KnowledgeBase from "./components/KnowledgeBase/KnowledgeBase";
import { DefaultToolbar } from "./components/DefaultToolbar";
import { TicketSearchBar } from "./components/TicketSearchBar";
import {
  useGetHelpdeskTicketsQuery,
  useGetHelpdeskViewsQuery,
  useDeleteHelpdeskViewMutation,
} from "../../../../redux/services/helpdeskApi";
import { useDispatch, useSelector } from "react-redux";
import type { RootState } from "../../../../redux/store";
import {
  setNewlyCreatedViewId,
  setSelectedViewId,
  setHelpdeskActiveView,
  clearAllStageTableFilters,
} from "../../../../redux/slices/helpdeskSlice";
import { CreateEditView } from "./components/CreateView/CreateEditView";
import type { HelpdeskViewItem } from "./components/AllTickets/utils/types";
import {
  HELPDESK_TICKETS_PAGE_SIZE,
  HELPDESK_VIEWS_PAGE_SIZE,
} from "./components/AllTickets/utils/constant";
import { getClientDomain } from "../../../../utils/functions/client-code";
import { isHelpdeskFeatureRequestsClient } from "./utils/function";
import { getHelpdeskLabels, getRegionLabel } from "../../../../utils/constants/mappings";
import { ViewTabsToolbar } from "./components/AllTickets/components/ViewTabsToolbar";
import DeleteViewModal from "./components/AllTickets/components/DeleteViewModal";
import { customToast } from "../../../../utils/custom-toast/custom-toast";
import ExportContactsModal from "../KanbanGrid/components/ExportContactsModal";
import ExportQueuedModal from "../KanbanGrid/components/ExportQueuedModal";
import { useCreateDataExportMutation } from "../../../../redux/services/settingsApi";

export const HelpDeskLayout = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tabId } = useParams<{ tabId: string }>();

  const helpdeskClientCode = getClientDomain();
  const authClientCode = useSelector(
    (state: RootState) => state?.auth?.client?.client_code
  );
  const region = useSelector(
    (state: RootState) => state?.auth?.client?.region
  );
  const helpdeskLabels = getHelpdeskLabels(region);
  const knowledgeBaseLabel = getRegionLabel(
    "knowledgeBaseSection",
    authClientCode
  );

  const isFeatureRequestsClient =
    isHelpdeskFeatureRequestsClient(helpdeskClientCode);

  const helpdeskFilterParam = searchParams.get("helpdesk_filter");

  const isFeatureRequestScope =
    isFeatureRequestsClient &&
    tabId === "all-tickets" &&
    helpdeskFilterParam === "feature-request";

  const activeTab = isFeatureRequestScope
    ? "feature-requests"
    : (tabId ?? "all-tickets");

  const newlyCreatedViewId = useSelector(
    (state: RootState) => state.helpdesk.newlyCreatedViewId
  );
  const selectedViewId = useSelector(
    (state: RootState) => state.helpdesk.selectedViewId
  );
  const helpdeskViewsFromStore = useSelector(
    (state: RootState) => state.helpdesk.helpdeskViews
  );

  const view = useSelector((state: RootState) => state.helpdesk.activeView);

  const sharedTableFilter = useSelector((state: RootState) => {
    const firstStageKey = state.helpdesk.stages?.[0]?.key;
    return firstStageKey
      ? state.helpdesk.tableFilters[firstStageKey]
      : undefined;
  });

  const [viewsFetchPage, setViewsFetchPage] = useState(1);
  const [hasMoreViews, setHasMoreViews] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<HelpdeskViewItem | null>(
    null
  );

  const [deleteHelpdeskView, { isLoading: isDeleting }] =
    useDeleteHelpdeskViewMutation();
  const [createDataExport, { isLoading: isExporting }] =
    useCreateDataExportMutation();

  const [isExportModalOpen, setIsExportModalOpen] = useState(false);
  const [isExportQueuedOpen, setIsExportQueuedOpen] = useState(false);
  const [exportedFile, setExportedFile] = useState<{
    name: string;
    format: string;
  } | null>(null);

  const {
    data: helpdeskViewsData,
    isLoading: isHelpdeskViewsInitialLoading,
    isFetching: isHelpdeskViewsFetching,
  } = useGetHelpdeskViewsQuery(
    { page: viewsFetchPage, limit: HELPDESK_VIEWS_PAGE_SIZE },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const isWaitingOnPageOneViewsFetch =
    isHelpdeskViewsFetching && viewsFetchPage === 1;

  const isLoadingMoreViews = isHelpdeskViewsFetching && viewsFetchPage > 1;

  const isAllTickets = selectedViewId === "all_tickets";

  const isWaitingForFirstViewsPage =
    helpdeskViewsFromStore === null && isHelpdeskViewsInitialLoading;

  const isSelectedViewInHelpdeskViews = useMemo(() => {
    if (selectedViewId === "all_tickets") return true;
    if (isWaitingForFirstViewsPage) return false;
    if (isWaitingOnPageOneViewsFetch) return false;
    const views = helpdeskViewsFromStore ?? undefined;
    if (views === undefined) return false;
    return views.some(
      (viewItem: HelpdeskViewItem) => viewItem?.id === selectedViewId
    );
  }, [
    helpdeskViewsFromStore,
    isWaitingForFirstViewsPage,
    selectedViewId,
    isWaitingOnPageOneViewsFetch,
  ]);

  const skipHelpdeskTicketsQuery =
    !isAllTickets && !isSelectedViewInHelpdeskViews;

  const {
    data: helpdeskTickets,
    isLoading: isHelpdeskTicketsLoading,
    isFetching: isHelpdeskTicketsFetching,
  } = useGetHelpdeskTicketsQuery(
    {
      page_size: String(HELPDESK_TICKETS_PAGE_SIZE),
      logical_operator: "and",
      ...(isAllTickets ? {} : { view_id: selectedViewId }),
      ...(isFeatureRequestScope ? { system_name: "Feature Requests" } : {}),
    },
    {
      skip: skipHelpdeskTicketsQuery,
      refetchOnMountOrArgChange: true,
    }
  );

  const helpdeskTicketsData = helpdeskTickets?.stages;

  const systemNameFilter = isFeatureRequestScope
    ? "Feature Requests"
    : undefined;

  const shouldShowDefaultToolbar = useMemo(() => {
    return (
      tabId !== "knowledge-base" &&
      tabId !== "ticket-reports" &&
      tabId !== "create-ticket"
    );
  }, [tabId]);

  const loadMoreViews = useCallback(() => {
    if (!hasMoreViews || isLoadingMoreViews) return;
    setViewsFetchPage((previous) => previous + 1);
  }, [hasMoreViews, isLoadingMoreViews]);

  const handleSetView = useCallback(
    (newView: string) => {
      dispatch(setHelpdeskActiveView(newView));
    },
    [dispatch]
  );

  const shouldShowViewTabsToolbar =
    activeTab === "all-tickets" || activeTab === "feature-requests";

  const handleSetSelectedViewId: React.Dispatch<React.SetStateAction<string>> =
    useCallback(
      (value: React.SetStateAction<string>) => {
        const newValue =
          typeof value === "function" ? value(selectedViewId) : value;
        if (newValue !== selectedViewId) {
          dispatch(clearAllStageTableFilters());
        }
        dispatch(setSelectedViewId(newValue));
      },
      [dispatch, selectedViewId]
    );

  const exportDefaultName = isAllTickets
    ? helpdeskLabels.allTickets
    : helpdeskViewsFromStore?.find((v) => v?.id === selectedViewId)?.name ??
      helpdeskLabels.allTickets;

  const exportTotalCount = useMemo(
    () =>
      ((helpdeskTicketsData ?? []) as { total?: number }[]).reduce(
        (sum, stage) => sum + (stage?.total ?? 0),
        0
      ),
    [helpdeskTicketsData]
  );

  const handleExport = useCallback(
    async ({
      exportName,
      fileFormat,
    }: {
      exportName: string;
      fileFormat: string;
    }) => {
      const entity_filter: Record<string, unknown> = {};
      if (!isAllTickets) entity_filter.view_id = selectedViewId;
      if (sharedTableFilter?.priorityFilter?.length) {
        entity_filter.priority = sharedTableFilter.priorityFilter;
      }
      if (sharedTableFilter?.assigneesFilter?.length) {
        entity_filter.assignees = sharedTableFilter.assigneesFilter;
      }
      try {
        await createDataExport({
          entity_type: "helpdesk_ticket",
          file_format: fileFormat,
          file_name_hint: exportName,
          entity_filter,
        }).unwrap();
        setIsExportModalOpen(false);
        setExportedFile({
          name: `${exportName}.${fileFormat}`,
          format: fileFormat,
        });
        setIsExportQueuedOpen(true);
      } catch {
        customToast("Failed to start export. Please try again.", "error");
      }
    },
    [isAllTickets, selectedViewId, sharedTableFilter, createDataExport]
  );

  const handleConfirmDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteHelpdeskView(deleteTarget.id).unwrap();
      customToast("View deleted successfully", "success");
      if (selectedViewId === deleteTarget.id) {
        handleSetSelectedViewId("all_tickets");
      }
      setDeleteTarget(null);
    } catch (error) {
      if ((error as { data: { detail: string } })?.data?.detail) {
        customToast(
          (error as { data: { detail: string } })?.data?.detail,
          "error"
        );
      } else {
        customToast("Failed to delete view", "error");
      }
    }
  }, [
    deleteTarget,
    deleteHelpdeskView,
    selectedViewId,
    handleSetSelectedViewId,
  ]);

  useEffect(() => {
    if (newlyCreatedViewId) {
      setViewsFetchPage(1);
    }
  }, [newlyCreatedViewId]);

  useEffect(() => {
    if (!newlyCreatedViewId) return;
    if (isWaitingOnPageOneViewsFetch) return;
    if (helpdeskViewsFromStore === null) return;

    const viewExists = helpdeskViewsFromStore.some(
      (item: HelpdeskViewItem) => item?.id === newlyCreatedViewId
    );

    dispatch(
      setSelectedViewId(viewExists ? newlyCreatedViewId : "all_tickets")
    );
    dispatch(setNewlyCreatedViewId(null));
  }, [
    newlyCreatedViewId,
    helpdeskViewsFromStore,
    isWaitingOnPageOneViewsFetch,
    dispatch,
  ]);

  useEffect(() => {
    if (helpdeskViewsData === undefined) return;
    setHasMoreViews(helpdeskViewsData?.has_next ?? false);
  }, [helpdeskViewsData]);

  useEffect(() => {
    if (selectedViewId === "all_tickets") return;
    if (helpdeskViewsFromStore === null) return;
    if (isWaitingOnPageOneViewsFetch) return;
    const viewExists = helpdeskViewsFromStore.some(
      (item: HelpdeskViewItem) => item?.id === selectedViewId
    );
    if (!viewExists) {
      dispatch(setSelectedViewId("all_tickets"));
    }
  }, [
    dispatch,
    helpdeskViewsFromStore,
    selectedViewId,
    isWaitingOnPageOneViewsFetch,
  ]);

  const renderTabContent = useCallback(
    (contentTabId: string) => {
      switch (contentTabId) {
        case "all-tickets":
        case "feature-requests":
          return (
            <AllTickets
              helpdeskTicketsData={helpdeskTicketsData}
              isLoading={isHelpdeskTicketsLoading}
              isFetching={isHelpdeskTicketsFetching}
              selectedViewId={selectedViewId}
              systemNameFilter={systemNameFilter}
            />
          );
        case "ticket-reports":
          return <TicketReports />;
        case "knowledge-base":
          return <KnowledgeBase />;
        case "create-ticket":
          return <CreateTicket />;
        default:
          return null;
      }
    },
    [
      helpdeskTicketsData,
      isHelpdeskTicketsLoading,
      isHelpdeskTicketsFetching,
      selectedViewId,
      systemNameFilter,
    ]
  );

  return (
    <div className="flex flex-col w-full h-full bg-pure-white dark:bg-pure-white-dk">
      <Header>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-[8px]">
            <h1 className="text-xl font-semibold text-verba-indigo dark:text-verba-indigo-dk">
              {helpdeskLabels.section}
            </h1>
            <hr className="border border-border-gray dark:border-border-gray-dk h-[16px] w-px" />
            <p className="text-md font-normal text-heading-text dark:text-heading-text-dk">
              {activeTab === "all-tickets" || activeTab === "feature-requests"
                ? helpdeskLabels.allTickets
                : activeTab === "knowledge-base"
                  ? knowledgeBaseLabel
                  : activeTab === "ticket-reports"
                    ? helpdeskLabels.ticketReports
                    : helpdeskLabels.createTicket}
            </p>
          </div>
          {(activeTab === "all-tickets" ||
            activeTab === "feature-requests") && <TicketSearchBar />}
        </div>
      </Header>
      <div
        className={`flex flex-col w-full h-full flex-1 min-h-0 border-l border-t rounded-tl-[6px] border-border-gray dark:border-border-gray-dk`}
        // data-helpdesk-scroll-container
      >
        {shouldShowDefaultToolbar && (
          <DefaultToolbar
            activeTab={activeTab}
            selectedViewId={selectedViewId}
            setSelectedViewId={handleSetSelectedViewId}
            hasMoreViews={hasMoreViews}
            isLoadingMoreViews={isLoadingMoreViews}
            onLoadMoreViews={loadMoreViews}
          />
        )}
        {shouldShowViewTabsToolbar && (
          <div className="px-4">
            <ViewTabsToolbar
              activeTab={activeTab}
              view={view}
              setView={handleSetView}
              selectedViewId={selectedViewId}
              setSelectedViewId={handleSetSelectedViewId}
              onDeleteView={setDeleteTarget}
              hasMoreViews={hasMoreViews}
              isLoadingMoreViews={isLoadingMoreViews}
              onLoadMoreViews={loadMoreViews}
              onExportClick={() => setIsExportModalOpen(true)}
            />
          </div>
        )}
        <div
          className={`w-full flex-1 min-h-0 overflow-auto ${visibleScrollbar(
            "[&::-webkit-scrollbar]:w-[8px]"
          )}`}
          data-helpdesk-scroll-container
        >
          {renderTabContent(activeTab)}
        </div>
      </div>
      <CreateEditView />

      <ExportContactsModal
        open={isExportModalOpen}
        onOpenChange={setIsExportModalOpen}
        totalCount={exportTotalCount}
        defaultName={exportDefaultName}
        loading={isExporting}
        onExport={handleExport}
      />

      {exportedFile && (
        <ExportQueuedModal
          open={isExportQueuedOpen}
          onOpenChange={setIsExportQueuedOpen}
          entityName={exportedFile.name}
          fileName={exportedFile.name}
          onViewImportsExports={() =>
            navigate("/dashboard/settings/imports_and_exports")
          }
        />
      )}

      {deleteTarget && (
        <DeleteViewModal
          open={!!deleteTarget}
          onOpenChange={(open) => {
            if (!open) setDeleteTarget(null);
          }}
          onConfirmDelete={handleConfirmDelete}
          view={deleteTarget}
          isDeleting={isDeleting}
        />
      )}
    </div>
  );
};
